import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Package, ArrowRight, RefreshCw, AlertTriangle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import '../styles/pages.css';

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { clearCart } = useCart();

  const orderId = searchParams.get('order_id');

  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setError('No order ID provided');
      setLoading(false);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/orders/${orderId}`);
      
      if (response.ok) {
        const orderData = await response.json();
        setOrder(orderData);
        
        // Clear cart if payment was successful
        if (orderData.status === 'paid' || orderData.payment_status === 'paid') {
          clearCart();
        }
        
        // If payment is still pending, retry after a few seconds (webhook might not have arrived yet)
        if (orderData.status === 'pending' || orderData.status === 'payment_pending') {
          if (retryCount < 3) {
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
            }, 3000);
          }
        }
      } else {
        setError('Order not found');
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId, clearCart, retryCount]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const getStatusInfo = () => {
    if (!order) return { icon: Clock, color: 'pending', title: 'Loading...', message: '' };
    
    const status = order.status || order.payment_status;
    
    switch (status) {
      case 'paid':
        return {
          icon: CheckCircle,
          color: 'success',
          title: 'Payment Successful!',
          message: 'Thank you for your order. We have received your payment and will process your order shortly. A confirmation email will be sent to you.'
        };
      case 'pending':
      case 'payment_pending':
        return {
          icon: Clock,
          color: 'pending',
          title: 'Payment Processing',
          message: 'Your payment is being processed. This page will update automatically once confirmed.'
        };
      case 'cancelled':
        return {
          icon: XCircle,
          color: 'error',
          title: 'Payment Cancelled',
          message: 'Your payment was cancelled. Your cart items have been preserved - you can try again when ready.'
        };
      case 'expired':
        return {
          icon: AlertTriangle,
          color: 'error',
          title: 'Payment Expired',
          message: 'Your payment session has expired. Please return to checkout to try again.'
        };
      case 'payment_failed':
      case 'failed':
        return {
          icon: XCircle,
          color: 'error',
          title: 'Payment Failed',
          message: 'Unfortunately, your payment could not be processed. Please try again or use a different payment method.'
        };
      default:
        return {
          icon: Clock,
          color: 'pending',
          title: 'Order Created',
          message: 'Your order has been created. Please complete the payment to proceed.'
        };
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    setRetryCount(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="order-confirmation-page">
        <div className="confirmation-container">
          <div className="loading-spinner">
            <Clock size={48} className="spin" />
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-confirmation-page">
        <div className="confirmation-container">
          <div className="confirmation-card error">
            <XCircle size={64} />
            <h1>Error</h1>
            <p>{error}</p>
            <Link to="/edible-cups" className="btn-primary">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;
  const isPending = order?.status === 'pending' || order?.status === 'payment_pending';
  const isSuccess = order?.status === 'paid';
  const isFailed = ['cancelled', 'expired', 'payment_failed', 'failed'].includes(order?.status);

  return (
    <div className="order-confirmation-page">
      <div className="confirmation-container">
        <div className={`confirmation-card ${statusInfo.color}`}>
          {/* Test Order Banner */}
          {order?.is_test_order && (
            <div className="test-order-banner">
              <AlertTriangle size={16} />
              <span>This is a test order</span>
            </div>
          )}

          <div className="status-icon">
            <StatusIcon size={64} />
          </div>
          
          <h1>{statusInfo.title}</h1>
          <p className="status-message">{statusInfo.message}</p>
          
          {/* Refresh button for pending orders */}
          {isPending && (
            <button onClick={handleRefresh} className="refresh-status-btn">
              <RefreshCw size={16} />
              Refresh Status
            </button>
          )}
          
          {order && (
            <div className="order-details">
              <div className="order-number">
                <span>Order Number:</span>
                <strong>{order.order_number || `#${order.id?.slice(0, 8).toUpperCase()}`}</strong>
              </div>
              
              <div className="order-info-grid">
                <div className="order-info-item">
                  <Package size={20} />
                  <div>
                    <span>Items</span>
                    <strong>{order.items?.length || 0} product(s)</strong>
                  </div>
                </div>
                <div className="order-info-item">
                  <span className="euro-sign">€</span>
                  <div>
                    <span>Total</span>
                    <strong>€{order.pricing?.finalTotal?.toFixed(2)}</strong>
                  </div>
                </div>
              </div>
              
              {/* Order Items Summary */}
              {isSuccess && order.items && (
                <div className="order-items-summary">
                  <h3>Order Items:</h3>
                  {order.items.map((item, index) => (
                    <div key={index} className="order-item-row">
                      <span>{item.name}</span>
                      <span>{item.variant}</span>
                      <span>€{item.price?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {order.customer && (
                <div className="shipping-info">
                  <h3>Shipping To:</h3>
                  <p>
                    {order.customer.firstName} {order.customer.lastName}<br />
                    {order.customer.companyName && <>{order.customer.companyName}<br /></>}
                    {order.customer.streetName} {order.customer.houseNumber}<br />
                    {order.customer.zipCode} {order.customer.city}<br />
                    {order.customer.country}
                  </p>
                </div>
              )}

              {/* Payment Method */}
              {order.payment_method && (
                <div className="payment-method-info">
                  <span>Payment Method:</span>
                  <strong>{order.payment_method === 'ideal' ? 'iDEAL' : order.payment_method}</strong>
                </div>
              )}
            </div>
          )}
          
          <div className="confirmation-actions">
            <Link to="/" className="btn-primary">
              Back to Home <ArrowRight size={18} />
            </Link>
            {isFailed && (
              <Link to="/shop" className="btn-secondary">
                Try Again
              </Link>
            )}
            {!isFailed && (
              <Link to="/edible-cups" className="btn-secondary">
                Continue Shopping
              </Link>
            )}
          </div>
          
          <p className="contact-note">
            Questions about your order? Contact us at{' '}
            <a href="mailto:info@magomeal.com">orders@magomeal.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
